import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { IntegrationsEntry } from '@/types/integrations';
import { InfoOutlined, Settings } from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type IntegrationCardProps = {
  item: IntegrationsEntry;
  setConfigModals: React.Dispatch<
    React.SetStateAction<{
      whatsapp: boolean;
      telegram: boolean;
      slack: boolean;
      hubspot: boolean;
      twilio: boolean;
      zoho: boolean;
    }>
  >;
};

function IntegrationCard({
  item,
  setConfigModals,
}: IntegrationCardProps): React.ReactNode {
  // const [enabled, setEnabled] = useState(item.enabled);
  const currentTheme = useAlchemystStoreForAi((store) => store.theme);

  const [infoOpen, setInfoOpen] = useState(false);

  const handleInfoOpen = () => {
    setInfoOpen(true);
  };

  const handleInfoClose = () => {
    setInfoOpen(false);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        padding: 3,
        paddingX: 6,
        marginBottom: 2,
        borderRadius: 2,
        background:
          currentTheme === 'dark'
            ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
            : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
      }}
      className={
        currentTheme === 'dark' ? 'custom-border' : 'custom-border_for_light'
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          flex: 1,
          maxWidth: '20vw',
          maxHeight: 'max-h-max',
        }}
      >
        <img src={item.icon} alt={item.name} width={80} height={80} />
        <Box marginLeft={2} sx={{}}>
          <Typography variant="h4">{item.name}</Typography>
          <Box sx={{ marginTop: 1, width: '90%', height: '20vh' }}>
            <Typography variant="body2" color="textSecondary">
              {item.description}
            </Typography>
          </Box>
          <Stack
            direction="row"
            sx={{ marginTop: 2, flexWrap: 'wrap', gap: 1 }}
          >
            {item.tags &&
              item.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  color="secondary"
                  variant="outlined"
                />
              ))}
          </Stack>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Configure Integration">
          <IconButton color="primary" aria-label="settings">
            <Settings
              fontSize={'medium'}
              onClick={() => {
                if (item.name.toLowerCase() === 'whatsapp') {
                  setConfigModals((prev) => ({
                    ...prev,
                    whatsapp: true,
                  }));
                } else if (item.name.toLowerCase() === 'telegram') {
                  setConfigModals((prev) => ({
                    ...prev,
                    telegram: true,
                  }));
                } else if (item.name.toLowerCase() === 'twilio') {
                  setConfigModals((prev) => ({
                    ...prev,
                    twilio: true,
                  }));
                } else if (item.name.toLowerCase() === 'zoho') {
                  setConfigModals((prev) => ({
                    ...prev,
                    zoho: true,
                  }));
                }
              }}
            />
          </IconButton>
        </Tooltip>

        {item.name.toLowerCase() === 'zoho' && (
          <>
            <Tooltip title="How to connect Zoho?">
              <IconButton
                color="primary"
                aria-label="info"
                onClick={handleInfoOpen}
              >
                <InfoOutlined fontSize="medium" />
              </IconButton>
            </Tooltip>

            <Dialog
              open={infoOpen}
              onClose={handleInfoClose}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Zoho Integration Guide</DialogTitle>
              <DialogContent>
                <Typography variant="body1" gutterBottom>
                  To fetch leads from Zoho, follow these steps:
                </Typography>

                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  {item.info.map((step: string, index: number) => (
                    <Box component="li" key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">{step}</Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="body2">
                  If Zoho is not yet connected, navigate to{' '}
                  <strong>Integrations &gt; Zoho CRM</strong> to begin the setup
                  process.
                </Typography>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleInfoClose} variant="outlined">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
}

export default IntegrationCard;
